import axios from 'axios';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { api } from 'services/api';
import { PAGE_SIZE } from 'services/constants/env';
import { notificationContainer } from 'utils/notificationContainer';
import {
	getAnnualFeeTypeLastListParams,
	getFeeClassLastListParams,
	getInvoicingFeeTypeLastListParams,
	getMonthlyFeeLastListParams,
} from './selectors';
import {
	clearAnnualFeeTypeDetails,
	clearFeeClassDetails,
	clearMonthlyFeeDetails,
	createAnnualFeeTypeRequest,
	createFeeClassRequest,
	createInvoicingFeeTypeRequest,
	createMonthlyFeeRequest,
	deleteAnnualFeeTypeFailure,
	deleteAnnualFeeTypeRequest,
	deleteAnnualFeeTypeSuccess,
	deleteFeeClassFailure,
	deleteFeeClassRequest,
	deleteFeeClassSuccess,
	deleteMonthlyFeeFailure,
	deleteMonthlyFeeRequest,
	deleteMonthlyFeeSuccess,
	getAnnualFeeTypeDetailsFailure,
	getAnnualFeeTypeDetailsRequest,
	getAnnualFeeTypeDetailsSuccess,
	getAnnualFeeTypeListFailure,
	getAnnualFeeTypeListRequest,
	getAnnualFeeTypeListSuccess,
	getAnnualFeeTypeOptionsFailure,
	getAnnualFeeTypeOptionsRequest,
	getAnnualFeeTypeOptionsSuccess,
	getFeeClassDetailsFailure,
	getFeeClassDetailsRequest,
	getFeeClassDetailsSuccess,
	getFeeClassListFailure,
	getFeeClassListRequest,
	getFeeClassListSuccess,
	getFeeClassOptionsFailure,
	getFeeClassOptionsRequest,
	getFeeClassOptionsSuccess,
	getInvoicingFeeTypeDetailsFailure,
	getInvoicingFeeTypeDetailsRequest,
	getInvoicingFeeTypeDetailsSuccess,
	getInvoicingFeeTypeListFailure,
	getInvoicingFeeTypeListRequest,
	getInvoicingFeeTypeListSuccess,
	getInvoicingFeeTypeOptionsFailure,
	getInvoicingFeeTypeOptionsRequest,
	getInvoicingFeeTypeOptionsSuccess,
	getMonthlyFeeDetailsFailure,
	getMonthlyFeeDetailsRequest,
	getMonthlyFeeDetailsSuccess,
	getMonthlyFeeListFailure,
	getMonthlyFeeListRequest,
	getMonthlyFeeListSuccess,
	getMonthlyFeeOptionsFailure,
	getMonthlyFeeOptionsRequest,
	getMonthlyFeeOptionsSuccess,
	saveAnnualFeeTypeFailure,
	saveAnnualFeeTypeSuccess,
	saveFeeClassFailure,
	saveFeeClassSuccess,
	saveInvoicingFeeTypeFailure,
	saveInvoicingFeeTypeSuccess,
	saveMonthlyFeeFailure,
	saveMonthlyFeeSuccess,
	updateAnnualFeeTypeRequest,
	updateFeeClassRequest,
	updateInvoicingFeeTypeRequest,
	updateMonthlyFeeRequest,
} from './reducer';
import {
	TAnnualFeeTypeDetails,
	TAnnualFeeTypeListResponse,
	TAnnualFeeTypeOption,
	TCreateOrUpdateAnnualFeeTypeRequest,
	TCreateOrUpdateFeeClassRequest,
	TCreateOrUpdateInvoicingFeeTypeRequest,
	TCreateOrUpdateMonthlyFeeRequest,
	TEasFeeIdRequest,
	TEasFeeManagementRequest,
	TFeeClassDetails,
	TFeeClassListResponse,
	TFeeClassOption,
	TInvoicingFeeTypeDetails,
	TInvoicingFeeTypeListResponse,
	TInvoicingFeeTypeOption,
	TMonthlyFeeDetails,
	TMonthlyFeeDetailsListResponse,
	TMonthlyFeeOption,
	TUpdateAnnualFeeTypeRequest,
	TUpdateFeeClassRequest,
	TUpdateInvoicingFeeTypeRequest,
	TUpdateMonthlyFeeRequest,
} from './types';

const DEFAULT_LIST_PARAMS: TEasFeeManagementRequest = {
	pageSize: PAGE_SIZE,
	pageNumber: 1,
};

/** Shows the message the API returned, falling back to the passed default. */
function notifyError(error: unknown, fallback: string) {
	if (axios.isAxiosError(error)) {
		notificationContainer(
			String(error?.response?.data?.message ?? error.message ?? fallback),
			'error',
		);
		return;
	}
	notificationContainer(fallback, 'error');
}

// ========================================== Annual fee types

function* getAnnualFeeTypeOptionsWorker() {
	try {
		const response: TAnnualFeeTypeOption[] = yield call(api.easFee.getAnnualFeeTypeOptions);
		yield put(getAnnualFeeTypeOptionsSuccess(response));
	} catch (error) {
		yield put(getAnnualFeeTypeOptionsFailure());
		notifyError(error, 'Failed to load annual fee types');
	}
}

function* getAnnualFeeTypeListWorker({ payload }: PayloadAction<TEasFeeManagementRequest>) {
	try {
		const response: TAnnualFeeTypeListResponse = yield call(
			api.easFee.getAnnualFeeTypeList,
			payload,
		);
		yield put(getAnnualFeeTypeListSuccess(response));
	} catch (error) {
		yield put(getAnnualFeeTypeListFailure());
		notifyError(error, 'Failed to load annual fee types');
	}
}

function* refetchAnnualFeeTypeList() {
	const lastParams: TEasFeeManagementRequest | null = yield select(getAnnualFeeTypeLastListParams);
	const response: TAnnualFeeTypeListResponse = yield call(
		api.easFee.getAnnualFeeTypeList,
		lastParams ?? DEFAULT_LIST_PARAMS,
	);
	yield put(getAnnualFeeTypeListSuccess(response));
}

function* getAnnualFeeTypeDetailsWorker({ payload }: PayloadAction<TEasFeeIdRequest>) {
	try {
		const response: TAnnualFeeTypeDetails = yield call(api.easFee.getAnnualFeeTypeDetails, payload);
		yield put(getAnnualFeeTypeDetailsSuccess(response));
	} catch (error) {
		yield put(getAnnualFeeTypeDetailsFailure());
		notifyError(error, 'Failed to load annual fee type');
	}
}

function* createAnnualFeeTypeWorker({
	payload,
}: PayloadAction<TCreateOrUpdateAnnualFeeTypeRequest>) {
	try {
		yield call(api.easFee.createAnnualFeeType, payload);
		yield call(refetchAnnualFeeTypeList);
		yield put(saveAnnualFeeTypeSuccess());
		notificationContainer('Annual fee type was successfully created', 'success');
	} catch (error) {
		yield put(saveAnnualFeeTypeFailure());
		notifyError(error, 'Failed to create annual fee type');
	}
}

function* updateAnnualFeeTypeWorker({ payload }: PayloadAction<TUpdateAnnualFeeTypeRequest>) {
	try {
		const response: TAnnualFeeTypeDetails = yield call(api.easFee.updateAnnualFeeType, payload);
		yield put(getAnnualFeeTypeDetailsSuccess(response));
		yield call(refetchAnnualFeeTypeList);
		yield put(saveAnnualFeeTypeSuccess());
		notificationContainer('Annual fee type was successfully updated', 'success');
	} catch (error) {
		yield put(saveAnnualFeeTypeFailure());
		notifyError(error, 'Failed to update annual fee type');
	}
}

function* deleteAnnualFeeTypeWorker({ payload }: PayloadAction<TEasFeeIdRequest>) {
	try {
		yield call(api.easFee.deleteAnnualFeeType, payload);
		yield put(clearAnnualFeeTypeDetails());
		yield call(refetchAnnualFeeTypeList);
		yield put(deleteAnnualFeeTypeSuccess());
		notificationContainer('Annual fee type was successfully deleted', 'success');
	} catch (error) {
		yield put(deleteAnnualFeeTypeFailure());
		notifyError(error, 'Failed to delete annual fee type');
	}
}

// ========================================== Monthly fees

function* getMonthlyFeeOptionsWorker() {
	try {
		const response: TMonthlyFeeOption[] = yield call(api.easFee.getMonthlyFeeOptions);
		yield put(getMonthlyFeeOptionsSuccess(response));
	} catch (error) {
		yield put(getMonthlyFeeOptionsFailure());
		notifyError(error, 'Failed to load monthly fees');
	}
}

function* getMonthlyFeeListWorker({ payload }: PayloadAction<TEasFeeManagementRequest>) {
	try {
		const response: TMonthlyFeeDetailsListResponse = yield call(
			api.easFee.getMonthlyFeeList,
			payload,
		);
		yield put(getMonthlyFeeListSuccess(response));
	} catch (error) {
		yield put(getMonthlyFeeListFailure());
		notifyError(error, 'Failed to load monthly fees');
	}
}

function* refetchMonthlyFeeList() {
	const lastParams: TEasFeeManagementRequest | null = yield select(getMonthlyFeeLastListParams);
	const response: TMonthlyFeeDetailsListResponse = yield call(
		api.easFee.getMonthlyFeeList,
		lastParams ?? DEFAULT_LIST_PARAMS,
	);
	yield put(getMonthlyFeeListSuccess(response));
}

function* getMonthlyFeeDetailsWorker({ payload }: PayloadAction<TEasFeeIdRequest>) {
	try {
		const response: TMonthlyFeeDetails = yield call(api.easFee.getMonthlyFeeDetails, payload);
		yield put(getMonthlyFeeDetailsSuccess(response));
	} catch (error) {
		yield put(getMonthlyFeeDetailsFailure());
		notifyError(error, 'Failed to load monthly fee');
	}
}

function* createMonthlyFeeWorker({ payload }: PayloadAction<TCreateOrUpdateMonthlyFeeRequest>) {
	try {
		yield call(api.easFee.createMonthlyFee, payload);
		yield call(refetchMonthlyFeeList);
		yield put(saveMonthlyFeeSuccess());
		notificationContainer('Monthly fee was successfully created', 'success');
	} catch (error) {
		yield put(saveMonthlyFeeFailure());
		notifyError(error, 'Failed to create monthly fee');
	}
}

function* updateMonthlyFeeWorker({ payload }: PayloadAction<TUpdateMonthlyFeeRequest>) {
	try {
		const response: TMonthlyFeeDetails = yield call(api.easFee.updateMonthlyFee, payload);
		yield put(getMonthlyFeeDetailsSuccess(response));
		yield call(refetchMonthlyFeeList);
		yield put(saveMonthlyFeeSuccess());
		notificationContainer('Monthly fee was successfully updated', 'success');
	} catch (error) {
		yield put(saveMonthlyFeeFailure());
		notifyError(error, 'Failed to update monthly fee');
	}
}

function* deleteMonthlyFeeWorker({ payload }: PayloadAction<TEasFeeIdRequest>) {
	try {
		yield call(api.easFee.deleteMonthlyFee, payload);
		yield put(clearMonthlyFeeDetails());
		yield call(refetchMonthlyFeeList);
		yield put(deleteMonthlyFeeSuccess());
		notificationContainer('Monthly fee was successfully deleted', 'success');
	} catch (error) {
		yield put(deleteMonthlyFeeFailure());
		notifyError(error, 'Failed to delete monthly fee');
	}
}

// ========================================== Invoicing fee types

function* getInvoicingFeeTypeOptionsWorker() {
	try {
		const response: TInvoicingFeeTypeOption[] = yield call(api.easFee.getInvoicingFeeTypeOptions);
		yield put(getInvoicingFeeTypeOptionsSuccess(response));
	} catch (error) {
		yield put(getInvoicingFeeTypeOptionsFailure());
		notifyError(error, 'Failed to load invoicing fee types');
	}
}

function* getInvoicingFeeTypeListWorker({ payload }: PayloadAction<TEasFeeManagementRequest>) {
	try {
		const response: TInvoicingFeeTypeListResponse = yield call(
			api.easFee.getInvoicingFeeTypeList,
			payload,
		);
		yield put(getInvoicingFeeTypeListSuccess(response));
	} catch (error) {
		yield put(getInvoicingFeeTypeListFailure());
		notifyError(error, 'Failed to load invoicing fee types');
	}
}

function* refetchInvoicingFeeTypeList() {
	const lastParams: TEasFeeManagementRequest | null = yield select(
		getInvoicingFeeTypeLastListParams,
	);
	const response: TInvoicingFeeTypeListResponse = yield call(
		api.easFee.getInvoicingFeeTypeList,
		lastParams ?? DEFAULT_LIST_PARAMS,
	);
	yield put(getInvoicingFeeTypeListSuccess(response));
}

function* getInvoicingFeeTypeDetailsWorker({ payload }: PayloadAction<TEasFeeIdRequest>) {
	try {
		const response: TInvoicingFeeTypeDetails = yield call(
			api.easFee.getInvoicingFeeTypeDetails,
			payload,
		);
		yield put(getInvoicingFeeTypeDetailsSuccess(response));
	} catch (error) {
		yield put(getInvoicingFeeTypeDetailsFailure());
		notifyError(error, 'Failed to load invoicing fee type');
	}
}

function* createInvoicingFeeTypeWorker({
	payload,
}: PayloadAction<TCreateOrUpdateInvoicingFeeTypeRequest>) {
	try {
		yield call(api.easFee.createInvoicingFeeType, payload);
		yield call(refetchInvoicingFeeTypeList);
		yield put(saveInvoicingFeeTypeSuccess());
		notificationContainer('Invoicing fee type was successfully created', 'success');
	} catch (error) {
		yield put(saveInvoicingFeeTypeFailure());
		notifyError(error, 'Failed to create invoicing fee type');
	}
}

function* updateInvoicingFeeTypeWorker({ payload }: PayloadAction<TUpdateInvoicingFeeTypeRequest>) {
	try {
		const response: TInvoicingFeeTypeDetails = yield call(
			api.easFee.updateInvoicingFeeType,
			payload,
		);
		yield put(getInvoicingFeeTypeDetailsSuccess(response));
		yield call(refetchInvoicingFeeTypeList);
		yield put(saveInvoicingFeeTypeSuccess());
		notificationContainer('Invoicing fee type was successfully updated', 'success');
	} catch (error) {
		yield put(saveInvoicingFeeTypeFailure());
		notifyError(error, 'Failed to update invoicing fee type');
	}
}

// ========================================== Fee classes

function* getFeeClassOptionsWorker() {
	try {
		const response: TFeeClassOption[] = yield call(api.easFee.getFeeClassOptions);
		yield put(getFeeClassOptionsSuccess(response));
	} catch (error) {
		yield put(getFeeClassOptionsFailure());
		notifyError(error, 'Failed to load fee classes');
	}
}

function* getFeeClassListWorker({ payload }: PayloadAction<TEasFeeManagementRequest>) {
	try {
		const response: TFeeClassListResponse = yield call(api.easFee.getFeeClassList, payload);
		yield put(getFeeClassListSuccess(response));
	} catch (error) {
		yield put(getFeeClassListFailure());
		notifyError(error, 'Failed to load fee classes');
	}
}

function* refetchFeeClassList() {
	const lastParams: TEasFeeManagementRequest | null = yield select(getFeeClassLastListParams);
	const response: TFeeClassListResponse = yield call(
		api.easFee.getFeeClassList,
		lastParams ?? DEFAULT_LIST_PARAMS,
	);
	yield put(getFeeClassListSuccess(response));
}

function* getFeeClassDetailsWorker({ payload }: PayloadAction<TEasFeeIdRequest>) {
	try {
		const response: TFeeClassDetails = yield call(api.easFee.getFeeClassDetails, payload);
		yield put(getFeeClassDetailsSuccess(response));
	} catch (error) {
		yield put(getFeeClassDetailsFailure());
		notifyError(error, 'Failed to load fee class');
	}
}

function* createFeeClassWorker({ payload }: PayloadAction<TCreateOrUpdateFeeClassRequest>) {
	try {
		yield call(api.easFee.createFeeClass, payload);
		yield call(refetchFeeClassList);
		yield put(saveFeeClassSuccess());
		notificationContainer('Fee class was successfully created', 'success');
	} catch (error) {
		yield put(saveFeeClassFailure());
		notifyError(error, 'Failed to create fee class');
	}
}

function* updateFeeClassWorker({ payload }: PayloadAction<TUpdateFeeClassRequest>) {
	try {
		const response: TFeeClassDetails = yield call(api.easFee.updateFeeClass, payload);
		yield put(getFeeClassDetailsSuccess(response));
		yield call(refetchFeeClassList);
		yield put(saveFeeClassSuccess());
		notificationContainer('Fee class was successfully updated', 'success');
	} catch (error) {
		yield put(saveFeeClassFailure());
		notifyError(error, 'Failed to update fee class');
	}
}

function* deleteFeeClassWorker({ payload }: PayloadAction<TEasFeeIdRequest>) {
	try {
		yield call(api.easFee.deleteFeeClass, payload);
		yield put(clearFeeClassDetails());
		yield call(refetchFeeClassList);
		yield put(deleteFeeClassSuccess());
		notificationContainer('Fee class was successfully deleted', 'success');
	} catch (error) {
		yield put(deleteFeeClassFailure());
		notifyError(error, 'Failed to delete fee class');
	}
}

export function* easFeeSaga() {
	yield takeLatest(getAnnualFeeTypeOptionsRequest.type, getAnnualFeeTypeOptionsWorker);
	yield takeLatest(getAnnualFeeTypeListRequest.type, getAnnualFeeTypeListWorker);
	yield takeLatest(getAnnualFeeTypeDetailsRequest.type, getAnnualFeeTypeDetailsWorker);
	yield takeLatest(createAnnualFeeTypeRequest.type, createAnnualFeeTypeWorker);
	yield takeLatest(updateAnnualFeeTypeRequest.type, updateAnnualFeeTypeWorker);
	yield takeLatest(deleteAnnualFeeTypeRequest.type, deleteAnnualFeeTypeWorker);

	yield takeLatest(getMonthlyFeeOptionsRequest.type, getMonthlyFeeOptionsWorker);
	yield takeLatest(getMonthlyFeeListRequest.type, getMonthlyFeeListWorker);
	yield takeLatest(getMonthlyFeeDetailsRequest.type, getMonthlyFeeDetailsWorker);
	yield takeLatest(createMonthlyFeeRequest.type, createMonthlyFeeWorker);
	yield takeLatest(updateMonthlyFeeRequest.type, updateMonthlyFeeWorker);
	yield takeLatest(deleteMonthlyFeeRequest.type, deleteMonthlyFeeWorker);

	yield takeLatest(getInvoicingFeeTypeOptionsRequest.type, getInvoicingFeeTypeOptionsWorker);
	yield takeLatest(getInvoicingFeeTypeListRequest.type, getInvoicingFeeTypeListWorker);
	yield takeLatest(getInvoicingFeeTypeDetailsRequest.type, getInvoicingFeeTypeDetailsWorker);
	yield takeLatest(createInvoicingFeeTypeRequest.type, createInvoicingFeeTypeWorker);
	yield takeLatest(updateInvoicingFeeTypeRequest.type, updateInvoicingFeeTypeWorker);

	yield takeLatest(getFeeClassOptionsRequest.type, getFeeClassOptionsWorker);
	yield takeLatest(getFeeClassListRequest.type, getFeeClassListWorker);
	yield takeLatest(getFeeClassDetailsRequest.type, getFeeClassDetailsWorker);
	yield takeLatest(createFeeClassRequest.type, createFeeClassWorker);
	yield takeLatest(updateFeeClassRequest.type, updateFeeClassWorker);
	yield takeLatest(deleteFeeClassRequest.type, deleteFeeClassWorker);
}
